// ============================================================================
// DETACHED DOOR (Physics-based door that flies off and can be picked up)
// ============================================================================

import CONFIG from '../config.js';
import { distance } from '../utils/helpers.js';
import Matter from 'matter-js';

const { Body } = Matter;

export class DetachedDoor {
    constructor(x, y, width, height, color, isLeftDoor, forceAmount, physics = null) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.color = color;
        this.physics = physics;
        this.isPickable = true;
        this.isCarried = false;
        this.carriedBy = null;  // Reference to player carrying this
        
        // Store the actual force/aggression used (for game logic like damage calculation)
        // This is NOT capped - represents the true "intent" behind the throw/spawn
        this.impactForce = forceAmount;

        // Random velocity based on force (with variance)
        // Use configurable spawn speed multiplier
        const variance = 0.5 + Math.random() * CONFIG.throwableSpawnVariance;
        const baseSpeed = (forceAmount * CONFIG.throwableSpawnSpeedMultiplier * variance);

        // Random direction with some bias away from cupboard
        const angleVariance = (Math.random() - 0.5) * Math.PI / 2;
        const baseAngle = isLeftDoor ? Math.PI : 0;
        const angle = baseAngle + angleVariance;

        const vx = Math.cos(angle) * baseSpeed;
        const vy = Math.sin(angle) * baseSpeed;

        // Random rotation speed (between min and max, not too fast)
        const rotationSpeed = CONFIG.throwableRotationSpeedMin + 
            Math.random() * (CONFIG.throwableRotationSpeedMax - CONFIG.throwableRotationSpeedMin);
        // Randomize direction
        const rotationDirection = Math.random() < 0.5 ? -1 : 1;
        const finalRotationSpeed = rotationSpeed * rotationDirection;

        // Random center of rotation (offset from center)
        this.rotationCenterX = (Math.random() - 0.5) * width * 0.5;
        this.rotationCenterY = (Math.random() - 0.5) * height * 0.5;
        this.rotation = 0;
        
        // Create Matter.js body (dynamic rectangle)
        if (physics) {
            const { CATEGORY, MASK } = physics.constructor;
            
            this.body = physics.createRectangle(x, y, width, height, {
                isStatic: false,
                frictionAir: CONFIG.throwableFrictionAir,  // Air friction (affects both linear and angular velocity)
                friction: 0.7,  // Increased surface friction for more realistic sliding
                restitution: CONFIG.wallBounceEnergyLoss,
                mass: 2,  // Light enough to be thrown
                collisionFilter: {
                    category: CATEGORY.THROWABLE,
                    mask: MASK.THROWABLE  // Collides with walls, furniture, doors (NOT player)
                }
            });
            
            // Set initial velocity and rotation (let physics handle rotation after this)
            physics.setVelocity(this.body, vx, vy);
            physics.setAngle(this.body, Math.random() * Math.PI * 2);
            Body.setAngularVelocity(this.body, finalRotationSpeed);
            
            physics.linkEntity(this, this.body);
            
            // Create sensor body for pickup detection
            const sensorRadius = (CONFIG.pickupRadius || 40) * 0.8; // 20% smaller
            this.sensorBody = physics.createSensor(x, y, sensorRadius);
            physics.linkEntity(this, this.sensorBody);
        } else {
            this.body = null;
            this.sensorBody = null;
            // Fallback to old system if no physics
            this.vx = vx;
            this.vy = vy;
            this.rotationSpeed = rotationSpeed;
        }
    }
    
    update(dt, tilemap) {
        if (this.isCarried) return;
        
        if (this.body) {
            // Apply manual angular damping (Matter.js doesn't have built-in angularDamping)
            // angularDamping is the fraction remaining after 1 second, so we apply it per frame
            if (this.body.angularVelocity !== 0) {
                const angularDamping = CONFIG.throwableAngularDamping;
                Body.setAngularVelocity(this.body, this.body.angularVelocity * Math.pow(angularDamping, dt));
            }
            
            // Sync position from physics body
            this.x = this.body.position.x;
            this.y = this.body.position.y;
            this.rotation = this.body.angle;
            
            // Update sensor body position
            if (this.sensorBody && this.physics) {
                this.physics.setPosition(this.sensorBody, this.x, this.y);
            }
        } else {
            // Fallback to old system
            this.x += this.vx * dt;
            this.y += this.vy * dt;
            this.rotation += this.rotationSpeed * dt;
            
            // Apply friction
            this.vx *= CONFIG.environmentFriction;
            this.vy *= CONFIG.environmentFriction;
            this.rotationSpeed *= CONFIG.environmentFriction;
            
            // Bounce off walls
            if (tilemap) {
                const tileSize = tilemap.tileSize;
                const halfW = this.width / 2;
                const halfH = this.height / 2;
                
                if (this.x - halfW < 0 || this.x + halfW > tilemap.cols * tileSize) {
                    this.vx *= -CONFIG.wallBounceEnergyLoss;
                    this.x = Math.max(halfW, Math.min(tilemap.cols * tileSize - halfW, this.x));
                }
                if (this.y - halfH < 0 || this.y + halfH > tilemap.rows * tileSize) {
                    this.vy *= -CONFIG.wallBounceEnergyLoss;
                    this.y = Math.max(halfH, Math.min(tilemap.rows * tileSize - halfH, this.y));
                }
            }
        }
    }
    
    // Remove duplicate update method below

    isNearPlayer(player) {
        if (this.isCarried || !player || !player.body || !this.sensorBody || !this.physics) return false;
        // Use physics sensor to detect if player is in range
        return this.physics.isPlayerInSensor(this.sensorBody, player.body);
    }

    pickup(player) {
        if (!this.isPickable || this.isCarried) return false;
        
        this.isCarried = true;
        this.carriedBy = player;
        
        if (this.body && this.physics) {
            // Stop current motion
            this.physics.setVelocity(this.body, 0, 0);
            Body.setAngularVelocity(this.body, 0);
            
            // Attach to player using constraint
            if (player.body) {
                this.physics.attachToPlayer(player.body, this.body, 0, -CONFIG.playerSize - 10);
            }
        }
        
        return true;
    }

    drop() {
        if (!this.isCarried) return;
        
        this.isCarried = false;
        
        if (this.body && this.physics) {
            // Detach from player
            this.physics.detachFromPlayer(this.body);
        }
        
        this.carriedBy = null;
    }

    throwAt(targetX, targetY, throwForce) {
        if (!this.isCarried) return;
        
        // Store the actual throw force (for damage calculation)
        // This is NOT capped - represents the true "intent" behind the throw
        this.impactForce = throwForce;
        
        // Detach from player
        if (this.body && this.physics) {
            this.physics.detachFromPlayer(this.body);
        }
        
        this.isCarried = false;
        this.carriedBy = null;
        
        // Calculate direction to target
        const dx = targetX - this.x;
        const dy = targetY - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist > 0) {
            // Use configurable throw speed multiplier
            const adjustedForce = throwForce * CONFIG.throwableThrowSpeedMultiplier;
            const vx = (dx / dist) * adjustedForce;
            const vy = (dy / dist) * adjustedForce;
            
            // Random rotation speed (between min and max, not too fast)
            const rotationSpeed = CONFIG.throwableRotationSpeedMin + 
                Math.random() * (CONFIG.throwableRotationSpeedMax - CONFIG.throwableRotationSpeedMin);
            const rotationDirection = Math.random() < 0.5 ? -1 : 1;
            const finalRotationSpeed = rotationSpeed * rotationDirection;
            
            if (this.body && this.physics) {
                this.physics.setVelocity(this.body, vx, vy);
                Body.setAngularVelocity(this.body, finalRotationSpeed);
            } else {
                // Fallback without physics
                this.vx = vx;
                this.vy = vy;
                this.rotationSpeed = finalRotationSpeed;
            }
        }
    }

    updateCarriedPosition(playerX, playerY) {
        // This is now handled by the physics constraint
        // But we still update our position for drawing if no physics
        if (this.isCarried && !this.body) {
            this.x = playerX;
            this.y = playerY - CONFIG.playerSize - 5;
        }
    }

    update(dt, tilemap) {
        if (this.isCarried) return;
        
        // Sync position from physics body
        if (this.body) {
            this.x = this.body.position.x;
            this.y = this.body.position.y;
            this.rotation = this.body.angle;
            
            // Update sensor body position
            if (this.sensorBody && this.physics) {
                this.physics.setPosition(this.sensorBody, this.x, this.y);
            }
        } else {
            // Fallback physics for non-Matter.js mode
            // No gravity in top-down view
            this.vx *= CONFIG.environmentFriction;
            this.vy *= CONFIG.environmentFriction;
            this.rotationSpeed *= CONFIG.environmentFriction;

            const newX = this.x + this.vx * dt;
            const newY = this.y + this.vy * dt;

            const tile = tilemap.getTileAt(newX, newY);
            if (!tile || !tilemap.tileTypes[tile.type].walkable) {
                const tileX = tilemap.getTileAt(newX, this.y);
                const tileY = tilemap.getTileAt(this.x, newY);

                if (!tileX || !tilemap.tileTypes[tileX.type].walkable) {
                    this.vx = -this.vx * CONFIG.wallBounceEnergyLoss;
                }
                if (!tileY || !tilemap.tileTypes[tileY.type].walkable) {
                    this.vy = -this.vy * CONFIG.wallBounceEnergyLoss;
                }
            } else {
                this.x = newX;
                this.y = newY;
            }

            this.rotation += this.rotationSpeed * dt;
        }
    }

    draw(ctx, camera) {
        ctx.save();
        
        let drawX = this.x;
        let drawY = this.y;
        let drawRotation = this.rotation;
        
        if (this.body) {
            drawX = this.body.position.x;
            drawY = this.body.position.y;
            drawRotation = this.body.angle;
        }
        
        ctx.translate(drawX, drawY);
        ctx.rotate(drawRotation);

        // Draw door
        ctx.fillStyle = this.color;
        ctx.fillRect(
            -this.width / 2 + this.rotationCenterX,
            -this.height / 2 + this.rotationCenterY,
            this.width,
            this.height
        );

        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        ctx.strokeRect(
            -this.width / 2 + this.rotationCenterX,
            -this.height / 2 + this.rotationCenterY,
            this.width,
            this.height
        );

        // Draw inner edge (darker)
        ctx.fillStyle = '#6B3410';
        ctx.fillRect(
            -this.width / 2 + this.rotationCenterX,
            -this.height / 2 + this.rotationCenterY,
            1,
            this.height
        );

        // Draw handle
        ctx.fillStyle = '#FFD700';
        ctx.beginPath();
        ctx.arc(
            -this.width / 2 + this.rotationCenterX + 3,
            this.rotationCenterY,
            2,
            0,
            Math.PI * 2
        );
        ctx.fill();

        ctx.restore();
    }
}
